/**
 * MANAGE COSTS MODAL — edit the two inputs to Safe to Spend without leaving Runway.
 *
 * Pure UI over endpoints that already exist:
 *   GET   /admin/financials/fixed-costs
 *   POST  /admin/financials/fixed-costs
 *   PATCH /admin/financials/fixed-costs/:id
 *   GET   /admin/financials/provider-balances
 *   PUT   /admin/financials/provider-balances/:provider
 * All are already global_admin gated at the router level.
 *
 * Frequency options are kept identical to the switch in
 * src/lib/financials/billingCycle.js#toMonthlyAmount. Anything outside that set
 * is deliberately NOT converted to a monthly figure and lands in the snapshot's
 * `unpriced` bucket, so a free-text frequency would silently drop a cost out of
 * the runway total. Constraining the input is the fix.
 */

import React, { useState, useEffect } from 'react';
import { X, Plus, AlertTriangle } from 'lucide-react';
import apiClient from '../../../lib/apiClient';

const FREQUENCIES = ['monthly', 'annual', 'quarterly', 'weekly', 'daily', 'one_time'];
const COST_TYPES = ['subscription', 'hosting', 'domain', 'telephony', 'data', 'tooling', 'other'];

const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Mirrors toMonthlyAmount server-side, for the live preview only. The server
// figure remains authoritative; this just shows the effect before saving.
const monthlyEquivalent = (amount, freq) => {
  const a = parseFloat(amount || 0);
  switch (freq) {
    case 'monthly': return a;
    case 'annual': return a / 12;
    case 'quarterly': return a / 3;
    case 'weekly': return a * 52 / 12;
    case 'daily': return a * 365 / 12;
    case 'one_time': return 0;
    default: return null;
  }
};

export default function ManageCostsModal({ open, onClose, onSaved, balances = [] }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { if (open) loadFixed(); /* eslint-disable-next-line */ }, [open]);

  const loadFixed = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await apiClient.get('/admin/financials/fixed-costs');
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  // Close on save/cancel and refetch the snapshot so Safe to Spend moves
  // immediately — but only refetch if something actually changed.
  const close = () => { if (dirty) onSaved?.(); onClose?.(); };

  if (!open) return null;

  const activeMonthly = rows
    .filter(r => r.is_active)
    .reduce((s, r) => s + (monthlyEquivalent(r.amount, r.billing_frequency) || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl my-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-lg">
          <div>
            <h2 className="text-xl font-bold text-gray-900">⚙️ Manage Costs</h2>
            <p className="text-sm text-gray-500 mt-1">
              These feed Safe to Spend directly. Changes apply as soon as you save.
            </p>
          </div>
          <button onClick={close} className="text-gray-400 hover:text-gray-600 p-1" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-8">
          {err && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{err}</div>
          )}

          {/* ── Section 1: fixed costs ─────────────────────────────────── */}
          <section>
            <div className="flex items-baseline justify-between mb-1 flex-wrap gap-2">
              <h3 className="text-lg font-semibold text-gray-900">Recurring Fixed Costs</h3>
              <span className="text-sm text-gray-600">
                Active total <span className="font-semibold text-gray-900">{money(activeMonthly)}/mo</span>
              </span>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Hosting, tooling, phone numbers, data providers — anything billed on a schedule.
              Usage-based spend (Anthropic, OpenAI, Twilio messages) does not belong here;
              it already flows through cost events and prepaid credit.
            </p>

            {loading ? (
              <p className="text-sm text-gray-500">Loading…</p>
            ) : (
              <div className="space-y-2">
                {rows.map(row => (
                  <FixedCostRow
                    key={row.id}
                    row={row}
                    onSaved={(updated) => {
                      setRows(rows.map(r => (r.id === updated.id ? updated : r)));
                      setDirty(true);
                    }}
                    onError={setErr}
                  />
                ))}
                {rows.length === 0 && <p className="text-sm text-gray-500">No fixed costs recorded yet.</p>}
              </div>
            )}

            <AddFixedCost
              onAdded={(created) => { setRows([...rows, created]); setDirty(true); }}
              onError={setErr}
            />
          </section>

          {/* ── Section 2: provider balances ───────────────────────────── */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Prepaid Provider Credit</h3>
            <p className="text-sm text-gray-500 mb-4">
              Auto-reload settings are console-only at both providers and can never be read over an API —
              whatever you record here is the only record of them.
            </p>
            <div className="space-y-3">
              {balances.map(b => (
                <BalanceEditor
                  key={b.id}
                  row={b}
                  onSaved={() => setDirty(true)}
                  onError={setErr}
                />
              ))}
              {balances.length === 0 && <p className="text-sm text-gray-500">No provider balances configured.</p>}
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            {dirty ? 'Changes saved. Runway will refresh when you close.' : 'No changes yet.'}
          </p>
          <button
            onClick={close}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            {dirty ? 'Done' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── one existing fixed cost, edited in place ───────────────────────────── */

function FixedCostRow({ row, onSaved, onError }) {
  const [form, setForm] = useState({
    description: row.description || '',
    provider: row.provider || '',
    amount: String(row.amount ?? ''),
    billing_frequency: row.billing_frequency || 'monthly',
    is_active: !!row.is_active
  });
  const [saving, setSaving] = useState(false);

  const changed =
    form.description !== (row.description || '') ||
    form.provider !== (row.provider || '') ||
    String(form.amount) !== String(row.amount ?? '') ||
    form.billing_frequency !== row.billing_frequency ||
    form.is_active !== !!row.is_active;

  const save = async (override = {}) => {
    setSaving(true); onError?.(null);
    try {
      const next = { ...form, ...override };
      const amount = parseFloat(next.amount);
      if (!Number.isFinite(amount) || amount < 0) throw new Error('Amount must be a non-negative number');
      // Whitelisted: the PATCH handler forwards req.body straight to .update(),
      // so an unknown key would be rejected by PostgREST.
      const r = await apiClient.patch(`/admin/financials/fixed-costs/${row.id}`, {
        description: next.description,
        provider: next.provider,
        amount,
        billing_frequency: next.billing_frequency,
        is_active: next.is_active
      });
      setForm({ ...next, amount: String(amount) });
      onSaved?.(r.data);
    } catch (e) {
      onError?.(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const monthly = monthlyEquivalent(form.amount, form.billing_frequency);

  return (
    <div className={`border rounded-lg p-3 ${form.is_active ? 'border-gray-200' : 'border-gray-200 bg-gray-50 opacity-70'}`}>
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
        <Input className="sm:col-span-4" label="Description" value={form.description}
               onChange={(v) => setForm({ ...form, description: v })} />
        <Input className="sm:col-span-3" label="Provider" value={form.provider}
               onChange={(v) => setForm({ ...form, provider: v })} />
        <Input className="sm:col-span-2" label="Amount" type="number" value={form.amount}
               onChange={(v) => setForm({ ...form, amount: v })} />
        <Select className="sm:col-span-2" label="Frequency" value={form.billing_frequency}
                options={FREQUENCIES} onChange={(v) => setForm({ ...form, billing_frequency: v })} />
        <div className="sm:col-span-1 flex items-center gap-2 pb-2">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => { const v = e.target.checked; setForm({ ...form, is_active: v }); save({ is_active: v }); }}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            title="Active"
          />
          <span className="text-xs text-gray-500">On</span>
        </div>
      </div>

      <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
        <p className="text-xs text-gray-500">
          {monthly === null
            ? <span className="text-amber-600 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Unrecognised frequency — excluded from the runway total
              </span>
            : <>≈ {money(monthly)}/mo{form.billing_frequency === 'one_time' && ' (one-time: not counted as recurring)'}</>}
        </p>
        {changed && (
          <button
            onClick={() => save()}
            disabled={saving}
            className="px-3 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>
    </div>
  );
}

/* ── add a new fixed cost ───────────────────────────────────────────────── */

function AddFixedCost({ onAdded, onError }) {
  const today = new Date().toLocaleDateString('en-CA');
  const blank = {
    description: '', provider: '', amount: '',
    billing_frequency: 'monthly', cost_type: 'subscription', start_date: today
  };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const add = async () => {
    setSaving(true); onError?.(null);
    try {
      const amount = parseFloat(form.amount);
      if (!form.description.trim()) throw new Error('Description is required');
      if (!form.provider.trim()) throw new Error('Provider is required');
      if (!Number.isFinite(amount) || amount <= 0) throw new Error('Amount must be greater than zero');
      // cost_type and start_date are required by the POST handler but are not
      // worth asking for every time — sensible defaults, both still editable.
      const r = await apiClient.post('/admin/financials/fixed-costs', { ...form, amount });
      setForm(blank);
      onAdded?.(r.data);
    } catch (e) {
      onError?.(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 border-2 border-dashed border-gray-300 rounded-lg p-3">
      <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
        <Plus className="w-4 h-4" /> Add a fixed cost
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
        <Input className="sm:col-span-3" label="Description" value={form.description}
               placeholder="Render web service" onChange={(v) => setForm({ ...form, description: v })} />
        <Input className="sm:col-span-2" label="Provider" value={form.provider}
               placeholder="Render" onChange={(v) => setForm({ ...form, provider: v })} />
        <Input className="sm:col-span-2" label="Amount" type="number" value={form.amount}
               placeholder="25.00" onChange={(v) => setForm({ ...form, amount: v })} />
        <Select className="sm:col-span-2" label="Frequency" value={form.billing_frequency}
                options={FREQUENCIES} onChange={(v) => setForm({ ...form, billing_frequency: v })} />
        <Select className="sm:col-span-2" label="Type" value={form.cost_type}
                options={COST_TYPES} onChange={(v) => setForm({ ...form, cost_type: v })} />
        <div className="sm:col-span-1">
          <button
            onClick={add}
            disabled={saving}
            className="w-full px-3 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? '…' : 'Add'}
          </button>
        </div>
      </div>
      <div className="mt-2">
        <Input className="sm:w-48" label="Start date" type="date" value={form.start_date}
               onChange={(v) => setForm({ ...form, start_date: v })} />
      </div>
    </div>
  );
}

/* ── provider balance editor (modal variant) ────────────────────────────── */

function BalanceEditor({ row, onSaved, onError }) {
  const [balance, setBalance] = useState(String(row.balance ?? ''));
  const [threshold, setThreshold] = useState(row.auto_reload_threshold ?? '');
  const [amount, setAmount] = useState(row.auto_reload_amount ?? '');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const save = async () => {
    setSaving(true); onError?.(null);
    try {
      const body = {
        auto_reload_threshold: threshold === '' ? null : threshold,
        auto_reload_amount: amount === '' ? null : amount
      };
      // Never push a value over a live-sourced balance: the next refresh
      // overwrites it anyway, so accepting the edit would be a lie.
      if (!row.live) body.balance = balance;
      await apiClient.put(`/admin/financials/provider-balances/${row.provider}`, body);
      setSavedAt(new Date());
      onSaved?.();
    } catch (e) {
      onError?.(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`border rounded-lg p-3 ${row.stale ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="font-medium text-gray-900 capitalize">{row.provider}</span>
        {row.live
          ? <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">live</span>
          : <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">manual</span>}
        {row.stale && (
          <span
            className="px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700"
            title="Usually overstates Safe to Spend (consumption drains it between updates); understates right after an auto-reload, when Mercury has already paid for credit this figure does not yet show."
          >
            stale — Safe to Spend is off
          </span>
        )}
        <span className="text-xs text-gray-500">
          as of {row.as_of ? new Date(row.as_of).toLocaleDateString() : '—'}
        </span>
      </div>

      {row.live_error && (
        <p className="text-xs text-amber-700 mb-2">
          Live read failed, editing the stored fallback: {row.live_error}
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
        <Input
          label="Balance" type="number"
          value={row.live ? row.balance : balance}
          disabled={row.live}
          hint={row.live ? 'Read live from provider' : 'From the provider console'}
          onChange={setBalance}
        />
        <Input label="Auto-reload below" type="number" value={threshold} onChange={setThreshold} hint="Console-only" />
        <Input label="Auto-reload amount" type="number" value={amount} onChange={setAmount} hint="Console-only" />
        <button
          onClick={save}
          disabled={saving}
          className="px-3 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      {savedAt && <p className="text-xs text-green-600 mt-2">Saved — date stamped to now.</p>}
    </div>
  );
}

/* ── inputs ─────────────────────────────────────────────────────────────── */

function Input({ label, value, onChange, type = 'text', placeholder, disabled, hint, className = '' }) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input
        type={type}
        step={type === 'number' ? '0.01' : undefined}
        min={type === 'number' ? '0' : undefined}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value)}
        className="w-full text-sm border border-gray-300 rounded px-2 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
      />
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

function Select({ label, value, options, onChange, className = '' }) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        className="w-full text-sm border border-gray-300 rounded px-2 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        {options.map(o => <option key={o} value={o}>{o.replace('_', ' ')}</option>)}
      </select>
    </div>
  );
}
